/**
 * PDF Generation Utilities
 * Functions to generate and print PDF reports
 */

import { formatDateShortYear, formatCurrencyRounded } from './formatters';

/**
 * Generate and print Stock Transfer Report PDF
 * @param {Object} params - Parameters for PDF generation
 */
export const generateStockTransferPDF = ({
  shiftedInData,
  shiftedOutData,
  startDate,
  endDate,
  shopName = 'Easy Sheets, Daily'
}) => {
  const dateRangeStr = startDate === endDate 
    ? formatDateShortYear(startDate) 
    : `${formatDateShortYear(startDate)} to ${formatDateShortYear(endDate)}`;

  const htmlContent = `
  <!DOCTYPE html>
  <html>
  <head>
    <title>Stock Transfer Report - ${dateRangeStr}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 20px; }
      .header { text-align: center; margin-bottom: 30px; }
      .header h1 { color: #2c3e50; margin: 0; }
      .header h2 { color: #7f8c8d; margin: 5px 0; }
      .section { margin-bottom: 30px; }
      .section-title { background: #3498db; color: white; padding: 10px; margin: 0; font-size: 16px; font-weight: bold; text-align: center; }
      table { width: 100%; border-collapse: collapse; margin-top: 0; }
      th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
      th { background-color: #f2f2f2; font-weight: bold; }
      .no-data { text-align: center; color: #7f8c8d; font-style: italic; }
      .brand-name { font-weight: bold; }
      .quantity { text-align: center; }
      .shop-name { font-style: italic; }
      .date { text-align: center; }
    </style>
  </head>
  <body>
    <div class="header">
      <h1>Stock Transfer Report</h1>
      <h2>${shopName}</h2>
      <h3>Date: ${dateRangeStr}</h3>
    </div>
    
    <div class="section">
      <h3 class="section-title">Stock Shifted In</h3>
      <table>
        <thead>
          <tr>
            <th>S.No</th>
            <th>Brand Name</th>
            <th>Size Code</th>
            <th>Quantity</th>
            <th>Shop Name (Retailer Code)</th>
            <th>Transfer Date</th>
          </tr>
        </thead>
        <tbody>
          ${shiftedInData.length === 0 ? 
            '<tr><td colspan="6" class="no-data">No stock shifted in for selected date</td></tr>' :
            shiftedInData.map((item, index) => `
              <tr>
                <td>${index + 1}</td>
                <td class="brand-name">${item.brandName} (${item.brandNumber})</td>
                <td>${item.sizeCode}</td>
                <td class="quantity">${item.quantity}</td>
                <td class="shop-name">${item.supplierName} (${item.supplierCode})</td>
                <td class="date">${new Date(item.transferDate).toLocaleDateString('en-GB')}</td>
              </tr>
            `).join('')
          }
        </tbody>
      </table>
    </div>
    
    <div class="section">
      <h3 class="section-title">Stock Shifted Out</h3>
      <table>
        <thead>
          <tr>
            <th>S.No</th>
            <th>Brand Name</th>
            <th>Size Code</th>
            <th>Quantity</th>
            <th>Shop Name (Retailer Code)</th>
            <th>Transfer Date</th>
          </tr>
        </thead>
        <tbody>
          ${shiftedOutData.length === 0 ? 
            '<tr><td colspan="6" class="no-data">No stock shifted out for selected date</td></tr>' :
            shiftedOutData.map((item, index) => `
              <tr>
                <td>${index + 1}</td>
                <td class="brand-name">${item.brandName} (${item.brandNumber})</td>
                <td>${item.sizeCode}</td>
                <td class="quantity">${item.quantity}</td>
                <td class="shop-name">${item.supplierName} (${item.supplierCode})</td>
                <td class="date">${new Date(item.transferDate).toLocaleDateString('en-GB')}</td>
              </tr>
            `).join('')
          }
        </tbody>
      </table>
    </div>
  </body>
  </html>
  `;

  const printWindow = window.open('', '_blank');
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  printWindow.print();
};

/**
 * Generate and print Sales Report PDF
 * @param {Object} params - Parameters for PDF generation
 */
export const generateSalesReportPDF = ({
  rows,
  totals,
  kindTotals,
  periodTotals,
  startDate,
  endDate,
  reportType,
  selectedYear,
  selectedMonth,
  shopName = 'Easy Sheets, Daily',
  hasAnyPriceDifference = false
}) => {
  const useDualColumns = hasAnyPriceDifference;
  const totalMrp = totals.totalMrp || 1;
  const totalActual = totals.totalActual || 1;

  const monthStr = reportType === 'monthly'
    ? new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '';
  
  const rangeStr = startDate === endDate 
    ? formatDateShortYear(startDate) 
    : `${formatDateShortYear(startDate)} to ${formatDateShortYear(endDate)}`;

  // Helper functions
  const formatNum = (n) => formatCurrencyRounded(n);
  
  const calc = (r) => {
    const pq = parseInt(r.packQuantity, 10) || 0;
    const cases = pq > 0 ? Math.floor((r.soldBottles || 0) / pq) : null;
    const loose = pq > 0 ? ((r.soldBottles || 0) % pq) : (r.soldBottles || 0);
    const mrp = (r.soldBottles || 0) * (r.mrp || 0);
    const actual = (r.soldBottles || 0) * (r.actualPrice || 0);
    const pctMrp = totalMrp > 0 ? (mrp * 100) / totalMrp : 0;
    const pctActual = totalActual > 0 ? (actual * 100) / totalActual : 0;
    return { cases, loose, mrp, actual, pctMrp, pctActual };
  };

  const rowHtml = (r, idx, bold = false) => {
    const { cases, loose, mrp, actual, pctMrp, pctActual } = calc(r);
    if (useDualColumns) {
      return `
        <tr${bold ? ' class="bold"' : ''}>
          ${idx !== null ? `<td class="center">${idx}</td>` : ''}
          <td>${r.baseName}</td>
          <td class="center">${r.sizeCode || ''}</td>
          <td>${cases != null ? cases.toFixed(2) : '-'}</td>
          <td>${loose}</td>
          <td>${formatNum(mrp)}</td>
          <td>${pctMrp.toFixed(2)}%</td>
          <td>${formatNum(actual)}</td>
          <td>${pctActual.toFixed(2)}%</td>
        </tr>
      `;
    } else {
      return `
        <tr${bold ? ' class="bold"' : ''}>
          ${idx !== null ? `<td class="center">${idx}</td>` : ''}
          <td>${r.baseName}</td>
          <td class="center">${r.sizeCode || ''}</td>
          <td>${cases != null ? cases.toFixed(2) : '-'}</td>
          <td>${loose}</td>
          <td>${formatNum(actual)}</td>
          <td>${pctActual.toFixed(2)}%</td>
        </tr>
      `;
    }
  };

  // Generate top 25
  const top25 = [...rows].sort((a,b) => 
    ((b.soldBottles||0)*(b.actualPrice||0)) - ((a.soldBottles||0)*(a.actualPrice||0))
  ).slice(0,25);
  const top25RowsHtml = top25.map((r,i) => rowHtml(r, i+1)).join('');

  // Generate kind-wise tables
  const kindOrder = ['Whisky','Beer','Brandy','Wine','Vodka','Rum','Gin','Liqueur','Tequila','Spirit','Ready to drink'];
  const canonicalKind = (raw) => {
    const t = (raw||'').toString().trim().toUpperCase();
    if (t.includes('WHISK')) return 'Whisky';
    if (t.includes('BEER')) return 'Beer';
    if (t.includes('BRANDY')) return 'Brandy';
    if (t.includes('WINE')) return 'Wine';
    if (t.includes('VODKA')) return 'Vodka';
    if (t.includes('RUM')) return 'Rum';
    if (t.includes('GIN')) return 'Gin';
    if (t.includes('LIQUEUR')||t.includes('LIQUOR')) return 'Liqueur';
    if (t.includes('TEQUILA')) return 'Tequila';
    if (t.includes('SPIRIT')) return 'Spirit';
    if (t.includes('READY')||t.includes('RTD')) return 'Ready to drink';
    return 'Other';
  };

  const byKind = new Map();
  rows.forEach(r => {
    const k = canonicalKind(r.brandKind);
    if (!byKind.has(k)) byKind.set(k,[]);
    byKind.get(k).push(r);
  });

  const allKindsHtml = kindOrder.map(kind => {
    const list = (byKind.get(kind)||[]).slice().sort((a,b) => 
      ((b.soldBottles||0)*(b.actualPrice||0)) - ((a.soldBottles||0)*(a.actualPrice||0))
    );
    if (list.length === 0) return '';
    
    const boldCount = list.length >= 5 ? 5 : 1;
    const rowsHtmlContent = list.map((r, idx) => rowHtml(r, idx+1, idx < boldCount)).join('');
    
    const mrpTotalRaw = list.reduce((s,r) => s + ((r.soldBottles||0)*(r.mrp||0)), 0);
    const actualTotalRaw = list.reduce((s,r) => s + ((r.soldBottles||0)*(r.actualPrice||0)), 0);
    const mrpSum = formatCurrencyRounded(mrpTotalRaw);
    const actualSum = formatCurrencyRounded(actualTotalRaw);
    const pctMrpSum = totalMrp > 0 ? ((mrpTotalRaw * 100) / totalMrp).toFixed(2) : '0.00';
    const pctActualSum = totalActual > 0 ? ((actualTotalRaw * 100) / totalActual).toFixed(2) : '0.00';
    
    if (useDualColumns) {
      return `
        <div class="subhead-left">${kind}</div>
        <table>
          <thead>
            <tr>
              <th rowspan="2">S.No</th>
              <th rowspan="2">Brand</th>
              <th rowspan="2">Size</th>
              <th colspan="2">Quantity</th>
              <th colspan="2">Sales (MRP)</th>
              <th colspan="2">Sales (Actual)</th>
            </tr>
            <tr>
              <th>Cases</th>
              <th>Bottles</th>
              <th>Value</th>
              <th>%</th>
              <th>Value</th>
              <th>%</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtmlContent}
          </tbody>
          <tfoot>
            <tr>
              <td colspan="5"><strong>Total</strong></td>
              <td class="right total-red">${mrpSum}</td>
              <td class="right total-red">${pctMrpSum}%</td>
              <td class="right total-red">${actualSum}</td>
              <td class="right total-red">${pctActualSum}%</td>
            </tr>
          </tfoot>
        </table>
      `;
    } else {
      return `
        <div class="subhead-left">${kind}</div>
        <table>
          <thead>
            <tr>
              <th rowspan="2">S.No</th>
              <th rowspan="2">Brand</th>
              <th rowspan="2">Size</th>
              <th colspan="2">Quantity</th>
              <th rowspan="2">Sales</th>
              <th rowspan="2">% of Total</th>
            </tr>
            <tr>
              <th>Cases</th>
              <th>Bottles</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtmlContent}
          </tbody>
          <tfoot>
            <tr>
              <td colspan="5"><strong>Total</strong></td>
              <td class="right total-red">${actualSum}</td>
              <td class="right total-red">${pctActualSum}%</td>
            </tr>
          </tfoot>
        </table>
      `;
    }
  }).join('');

  // Generate brand-wise table
  const getBaseName = (productName) => {
    return (productName || '').replace(/\s+(90ml|180ml|375ml|750ml|1000ml|2000ml|60ml|500ml|650ml|330ml|275ml).*$/i, '').trim();
  };

  const byBase = new Map();
  rows.forEach(r => {
    const base = r.baseName || getBaseName(r.brandName);
    if (!byBase.has(base)) byBase.set(base, []);
    byBase.get(base).push(r);
  });

  const brandWiseHtml = (() => {
    const groups = Array.from(byBase.entries()).map(([base, list]) => {
      const groupOrder = Math.min(...list.map(r => (typeof r.order === 'number' ? r.order : 9999)));
      return [base, list, groupOrder];
    }).sort((a, b) => a[2] - b[2]);

    const body = groups.map(([base, list], gIdx) => {
      const sorted = list.slice().sort((a,b) => {
        const ao = (typeof a.order === 'number' ? a.order : 9999);
        const bo = (typeof b.order === 'number' ? b.order : 9999);
        return ao - bo;
      });
      
      return sorted.map((r, idx) => {
        const { cases, loose, mrp, actual, pctMrp, pctActual } = calc(r);
        
        if (useDualColumns) {
          return `
            <tr>
              ${idx === 0 ? `<td class="center" rowSpan="${sorted.length}">${gIdx + 1}</td>` : ''}
              ${idx === 0 ? `<td rowSpan="${sorted.length}">${base}</td>` : ''}
              <td class="center">${r.sizeCode || ''}</td>
              <td>${cases != null ? cases.toFixed(2) : '-'}</td>
              <td>${loose}</td>
              <td>${formatNum(mrp)}</td>
              <td>${pctMrp.toFixed(2)}%</td>
              <td>${formatNum(actual)}</td>
              <td>${pctActual.toFixed(2)}%</td>
            </tr>
          `;
        } else {
          return `
            <tr>
              ${idx === 0 ? `<td class="center" rowSpan="${sorted.length}">${gIdx + 1}</td>` : ''}
              ${idx === 0 ? `<td rowSpan="${sorted.length}">${base}</td>` : ''}
              <td class="center">${r.sizeCode || ''}</td>
              <td>${cases != null ? cases.toFixed(2) : '-'}</td>
              <td>${loose}</td>
              <td>${formatNum(actual)}</td>
              <td>${pctActual.toFixed(2)}%</td>
            </tr>
          `;
        }
      }).join('');
    }).join('');

    const totalMrpVal = formatCurrencyRounded(rows.reduce((s,r) => s + ((r.soldBottles||0)*(r.mrp||0)), 0));
    const totalActualVal = formatCurrencyRounded(rows.reduce((s,r) => s + ((r.soldBottles||0)*(r.actualPrice||0)), 0));
    
    if (useDualColumns) {
      return `
        <table>
          <thead>
            <tr>
              <th rowspan="2">S.No</th>
              <th rowspan="2">Brand</th>
              <th rowspan="2">Size</th>
              <th colspan="2">Quantity</th>
              <th colspan="2">Sales (MRP)</th>
              <th colspan="2">Sales (Actual)</th>
            </tr>
            <tr>
              <th>Cases</th>
              <th>Bottles</th>
              <th>Value</th>
              <th>%</th>
              <th>Value</th>
              <th>%</th>
            </tr>
          </thead>
          <tbody>
            ${body}
          </tbody>
          <tfoot>
            <tr>
              <td colspan="5"><strong>Total</strong></td>
              <td class="right total-red">${totalMrpVal}</td>
              <td class="right total-red">100.00%</td>
              <td class="right total-red">${totalActualVal}</td>
              <td class="right total-red">100.00%</td>
            </tr>
          </tfoot>
        </table>
      `;
    } else {
      return `
        <table>
          <thead>
            <tr>
              <th rowspan="2">S.No</th>
              <th rowspan="2">Brand</th>
              <th rowspan="2">Size</th>
              <th colspan="2">Quantity</th>
              <th rowspan="2">Sales</th>
              <th rowspan="2">% of Total</th>
            </tr>
            <tr>
              <th>Cases</th>
              <th>Bottles</th>
            </tr>
          </thead>
          <tbody>
            ${body}
          </tbody>
          <tfoot>
            <tr>
              <td colspan="5"><strong>Total</strong></td>
              <td class="right total-red">${totalActualVal}</td>
              <td class="right total-red">100.00%</td>
            </tr>
          </tfoot>
        </table>
      `;
    }
  })();

  const kindSharePctTotal = (() => {
    try {
      const mrpSum = (kindTotals || []).reduce((sum, k) => sum + ((k && k.valueMrp) || 0), 0);
      return totalMrp > 0 ? ((mrpSum * 100) / totalMrp).toFixed(2) : '0.00';
    } catch (_) {
      return '0.00';
    }
  })();

  const kindShareActualPctTotal = (() => {
    try {
      const actualSum = (kindTotals || []).reduce((sum, k) => sum + ((k && k.valueActual) || 0), 0);
      return totalActual > 0 ? ((actualSum * 100) / totalActual).toFixed(2) : '0.00';
    } catch (_) {
      return '0.00';
    }
  })();

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Sales Report - ${shopName}</title>
  <style>
    @page { margin: 12mm; size: A4 portrait; }
    body { font-family: Arial, sans-serif; font-size: 11px; }
    .header { text-align: center; margin-bottom: 10px; }
    .shop { font-weight: 800; font-size: 22px; }
    .title { font-weight: 700; font-size: 14px; text-align: center; margin-bottom: 12px; }
    .report { font-weight: 800; font-size: 18px; text-transform: uppercase; }
    .meta { font-weight: 600; font-size: 12px; }
    .month { font-weight: 600; font-size: 14px; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; }
    th, td { border: 1px solid #000; padding: 4px; text-align: center; }
    th { background: #f0f0f0; }
    .right { text-align: center; }
    .center { text-align: center; }
    .section { margin-top: 18px; }
    .page-break { page-break-before: always; }
    .bold { font-weight: bold; }
    .subhead-left { text-align: left; font-weight: bold; margin: 6px 0; }
    .total-red { color: #d00; font-weight: bold; }
    thead { display: table-header-group; }
    tfoot { display: table-row-group; }
    tfoot tr { page-break-inside: avoid; }
    .summary { width: 60%; margin: 6px auto 0 auto; }
    .summary-section { margin: 18px 0 30px; }
    .summary th, .summary td { font-weight: bold; font-size: 14px; }
    .summary-title { font-size: 16px; }
    .share-table { width: 80%; margin: 10px auto 0 auto; }
    .share-section { margin-top: 36px; }
    .share-section .title { margin-bottom: 8px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="shop">${shopName}</div>
    <div class="report">SALES REPORT</div>
    ${monthStr ? `<div class="month">${monthStr}</div>` : ''}
    <div class="meta">${rangeStr}</div>
  </div>

  <div class="section summary-section">
    <table class="summary">
      <thead>
        <tr>
          ${useDualColumns ? '<th>Total Sale Value (MRP)</th><th>Total Sale Value (Actual)</th><th>Excess</th>' : '<th>Total Sale Value</th>'}
        </tr>
      </thead>
      <tbody>
        <tr>
          ${useDualColumns ? `<td class="center"><strong>${formatCurrencyRounded(totals.totalMrp)}</strong></td><td class="center"><strong>${formatCurrencyRounded(totals.totalActual)}</strong></td><td class="center" style="color: ${totals.totalActual - totals.totalMrp >= 0 ? '#28a745' : '#dc3545'}"><strong>${formatCurrencyRounded(totals.totalActual - totals.totalMrp)}</strong></td>` : `<td class="center"><strong>${formatCurrencyRounded(totals.totalActual)}</strong></td>`}
        </tr>
      </tbody>
    </table>
  </div>

  <div class="section">
    <div class="title">${reportType === 'yearly' ? 'Sales Report - Month Wise' : 'Sales Report - Day Wise'}</div>
    <table class="share-table">
      <thead>
        <tr>
          <th>S.No</th>
          <th>${reportType === 'yearly' ? 'Month' : 'Date'}</th>
          ${useDualColumns ? '<th>Sales (MRP)</th><th>Sales (Actual)</th><th>Excess</th>' : '<th>Sales</th>'}
        </tr>
      </thead>
      <tbody>
        ${(periodTotals || []).map((p, idx) => useDualColumns ? `
          <tr>
            <td class="center">${idx + 1}</td>
            <td>${p.label}</td>
            <td class="right">${formatCurrencyRounded(p.mrpTotal || 0)}</td>
            <td class="right">${formatCurrencyRounded(p.actualTotal || 0)}</td>
            <td class="right" style="color: ${(p.actualTotal - p.mrpTotal) >= 0 ? '#28a745' : '#dc3545'}">${formatCurrencyRounded((p.actualTotal || 0) - (p.mrpTotal || 0))}</td>
          </tr>
        ` : `
          <tr>
            <td class="center">${idx + 1}</td>
            <td>${p.label}</td>
            <td class="right">${formatCurrencyRounded(p.actualTotal || 0)}</td>
          </tr>
        `).join('')}
      </tbody>
      <tfoot>
        ${useDualColumns ? `
        <tr>
          <td colspan="2"><strong>Total</strong></td>
          <td class="right total-red">${formatCurrencyRounded((periodTotals || []).reduce((s, p) => s + (p.mrpTotal || 0), 0))}</td>
          <td class="right total-red">${formatCurrencyRounded((periodTotals || []).reduce((s, p) => s + (p.actualTotal || 0), 0))}</td>
          <td class="right total-red" style="color: ${((periodTotals || []).reduce((s, p) => s + (p.actualTotal || 0), 0) - (periodTotals || []).reduce((s, p) => s + (p.mrpTotal || 0), 0)) >= 0 ? '#28a745' : '#dc3545'}">${formatCurrencyRounded(((periodTotals || []).reduce((s, p) => s + (p.actualTotal || 0), 0)) - ((periodTotals || []).reduce((s, p) => s + (p.mrpTotal || 0), 0)))}</td>
        </tr>
        ` : `
        <tr>
          <td colspan="2"><strong>Total</strong></td>
          <td class="right total-red">${formatCurrencyRounded((periodTotals || []).reduce((s, p) => s + (p.actualTotal || 0), 0))}</td>
        </tr>
        `}
      </tfoot>
    </table>
  </div>

  <div class="page-break"></div>
  <div class="header">
    <div class="shop">${shopName}</div>
    <div class="report">SALES REPORT</div>
    ${monthStr ? `<div class="month">${monthStr}</div>` : ''}
    <div class="meta">${rangeStr}</div>
  </div>

  <div class="section">
    <div class="title">Sales - Brand Type wise</div>
    <table class="share-table">
      <thead>
        <tr>
          <th>S.No</th>
          <th>Type</th>
          ${useDualColumns ? '<th>Sales (MRP)</th><th>% of Total</th><th>Sales (Actual)</th><th>% of Total</th>' : '<th>Sales</th><th>% of Total</th>'}
        </tr>
      </thead>
      <tbody>
        ${kindTotals.map((k, idx) => useDualColumns ? `
          <tr>
            <td class="center">${idx + 1}</td>
            <td>${k.label}</td>
            <td class="right">${formatCurrencyRounded(k.valueMrp)}</td>
            <td class="right">${k.shareMrp.toFixed(2)}%</td>
            <td class="right">${formatCurrencyRounded(k.valueActual)}</td>
            <td class="right">${k.shareActual.toFixed(2)}%</td>
          </tr>
        ` : `
          <tr>
            <td class="center">${idx + 1}</td>
            <td>${k.label}</td>
            <td class="right">${formatCurrencyRounded(k.valueActual)}</td>
            <td class="right">${k.shareActual.toFixed(2)}%</td>
          </tr>
        `).join('')}
      </tbody>
      <tfoot>
        <tr>
          ${useDualColumns ? `
            <td colspan="2"><strong>Total</strong></td>
            <td class="right total-red">${formatCurrencyRounded(totals.totalMrp)}</td>
            <td class="right total-red">${kindSharePctTotal}%</td>
            <td class="right total-red">${formatCurrencyRounded(totals.totalActual)}</td>
            <td class="right total-red">${kindShareActualPctTotal}%</td>
          ` : `
            <td colspan="2"><strong>Total</strong></td>
            <td class="right total-red">${formatCurrencyRounded(totals.totalActual)}</td>
            <td class="right total-red">${kindShareActualPctTotal}%</td>
          `}
        </tr>
      </tfoot>
    </table>
  </div>

  <div class="page-break"></div>
  <div class="header">
    <div class="shop">${shopName}</div>
    <div class="report">SALES REPORT</div>
    ${monthStr ? `<div class="month">${monthStr}</div>` : ''}
    <div class="meta">${rangeStr}</div>
  </div>

  <div class="section">
    <div class="title">Sales Report - Top 25</div>
    <table>
      <thead>
        ${useDualColumns ? `
        <tr>
          <th rowspan="2">S.No</th>
          <th rowspan="2">Brand</th>
          <th rowspan="2">Size</th>
          <th colspan="2">Quantity</th>
          <th colspan="2">Sales (MRP)</th>
          <th colspan="2">Sales (Actual)</th>
        </tr>
        <tr>
          <th>Cases</th>
          <th>Bottles</th>
          <th>Value</th>
          <th>%</th>
          <th>Value</th>
          <th>%</th>
        </tr>
        ` : `
        <tr>
          <th rowspan="2">S.No</th>
          <th rowspan="2">Brand</th>
          <th rowspan="2">Size</th>
          <th colspan="2">Quantity</th>
          <th rowspan="2">Sales</th>
          <th rowspan="2">% of Total</th>
        </tr>
        <tr>
          <th>Cases</th>
          <th>Bottles</th>
        </tr>
        `}
      </thead>
      <tbody>
        ${top25RowsHtml}
      </tbody>
      <tfoot>
        ${useDualColumns ? `
        <tr>
          <td colspan="5"><strong>Total</strong></td>
          <td class="right total-red">${formatCurrencyRounded(top25.reduce((s,r) => s + ((r.soldBottles||0)*(r.mrp||0)),0))}</td>
          <td class="right total-red">${(((top25.reduce((s,r) => s + ((r.soldBottles||0)*(r.mrp||0)),0)) * 100) / totalMrp).toFixed(2)}%</td>
          <td class="right total-red">${formatCurrencyRounded(top25.reduce((s,r) => s + ((r.soldBottles||0)*(r.actualPrice||0)),0))}</td>
          <td class="right total-red">${(((top25.reduce((s,r) => s + ((r.soldBottles||0)*(r.actualPrice||0)),0)) * 100) / totalActual).toFixed(2)}%</td>
        </tr>
        ` : `
        <tr>
          <td colspan="5"><strong>Total</strong></td>
          <td class="right total-red">${formatCurrencyRounded(top25.reduce((s,r) => s + ((r.soldBottles||0)*(r.actualPrice||0)),0))}</td>
          <td class="right total-red">${(((top25.reduce((s,r) => s + ((r.soldBottles||0)*(r.actualPrice||0)),0)) * 100) / totalActual).toFixed(2)}%</td>
        </tr>
        `}
      </tfoot>
    </table>
  </div>

  <div class="page-break"></div>
  <div class="header">
    <div class="shop">${shopName}</div>
    <div class="report">SALES REPORT</div>
    ${monthStr ? `<div class="month">${monthStr}</div>` : ''}
    <div class="meta">${rangeStr}</div>
  </div>

  <div class="section">
    <div class="title">Sales Report - All Brands</div>
    ${allKindsHtml}
  </div>

  <div class="page-break"></div>
  <div class="header">
    <div class="shop">${shopName}</div>
    <div class="report">SALES REPORT</div>
    ${monthStr ? `<div class="month">${monthStr}</div>` : ''}
    <div class="meta">${rangeStr}</div>
  </div>

  <div class="section">
    <div class="title">Sales Report - Brand Wise</div>
    ${brandWiseHtml}
  </div>

  <div class="page-break"></div>
  <div class="header">
    <div class="shop">${shopName}</div>
    <div class="report">SALES REPORT</div>
    ${monthStr ? `<div class="month">${monthStr}</div>` : ''}
    <div class="meta">${rangeStr}</div>
  </div>

  <div class="section">
    <div class="title">${reportType === 'yearly' ? 'Sales Report - Month Wise' : 'Sales Report - Day Wise'}</div>
    <table>
      <thead>
        <tr>
          <th>S.No</th>
          <th>${reportType === 'yearly' ? 'Month' : 'Date'}</th>
          ${useDualColumns ? '<th>Sales (MRP)</th><th>Sales (Actual)</th><th>Excess</th>' : '<th>Sales</th>'}
        </tr>
      </thead>
      <tbody>
        ${(periodTotals || []).map((p, idx) => useDualColumns ? `
          <tr>
            <td class="center">${idx + 1}</td>
            <td>${p.label}</td>
            <td class="right">${formatCurrencyRounded(p.mrpTotal || 0)}</td>
            <td class="right">${formatCurrencyRounded(p.actualTotal || 0)}</td>
            <td class="right" style="color: ${(p.actualTotal - p.mrpTotal) >= 0 ? '#28a745' : '#dc3545'}">${formatCurrencyRounded((p.actualTotal || 0) - (p.mrpTotal || 0))}</td>
          </tr>
        ` : `
          <tr>
            <td class="center">${idx + 1}</td>
            <td>${p.label}</td>
            <td class="right">${formatCurrencyRounded(p.actualTotal || 0)}</td>
          </tr>
        `).join('')}
      </tbody>
      <tfoot>
        ${useDualColumns ? `
        <tr>
          <td colspan="2"><strong>Total</strong></td>
          <td class="right total-red">${formatCurrencyRounded((periodTotals || []).reduce((s, p) => s + (p.mrpTotal || 0), 0))}</td>
          <td class="right total-red">${formatCurrencyRounded((periodTotals || []).reduce((s, p) => s + (p.actualTotal || 0), 0))}</td>
          <td class="right total-red" style="color: ${((periodTotals || []).reduce((s, p) => s + (p.actualTotal || 0), 0) - (periodTotals || []).reduce((s, p) => s + (p.mrpTotal || 0), 0)) >= 0 ? '#28a745' : '#dc3545'}">${formatCurrencyRounded(((periodTotals || []).reduce((s, p) => s + (p.actualTotal || 0), 0)) - ((periodTotals || []).reduce((s, p) => s + (p.mrpTotal || 0), 0)))}</td>
        </tr>
        ` : `
        <tr>
          <td colspan="2"><strong>Total</strong></td>
          <td class="right total-red">${formatCurrencyRounded((periodTotals || []).reduce((s, p) => s + (p.actualTotal || 0), 0))}</td>
        </tr>
        `}
      </tfoot>
    </table>
  </div>

</body>
</html>
`;

  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
  setTimeout(() => { w.print(); w.close(); }, 600);
};

